import * as R from 'ramda';
import { createSelector } from 'reselect';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature dispatch-planner
import * as H from './helpers';
//////////////////////////////////////////////////

const emptyObject = {};

const selectBuilderStore = (state) => state.dispatchPlannerV2;
const selectCurrentRoute = (state) => state.dispatchPlannerV2.currentRoute;
const selectCurrentRouteItems = (state) => state.dispatchPlannerV2.currentRouteItems;
const selectCurrentRouteContainers = (state) => state.dispatchPlannerV2.currentRouteContainers;
const selectCurrentRouteInitialState = (state) => state.dispatchPlannerV2.currentRouteInitialState;
const selectCurrentBranchConfigs = (state) => state.dispatchPlannerV2.currentBranchConfigs;
const selectCloList = (state) => state.dispatchPlannerV2.cloList;
const selectCrossDockCloList = (state) => state.dispatchPlannerV2.crossDockCloList;
const selectRouteErrors = (state) => state.dispatchPlannerV2.routeErrors;
const selectScheduleEditorHistory = (state) => state.dispatchPlannerV2.scheduleEditorHistory;

const selectCurrentRouteTels = (state) => R.pathOr(
  emptyObject,
  [GC.SYSTEM_LIST_TELS],
  selectCurrentRoute(state),
);

const selectCurrentRouteClos = (state) => R.pathOr(
  emptyObject,
  [GC.SYSTEM_LIST_CLOS],
  selectCurrentRoute(state),
);

const selectInitialRouteTels = (state) => R.pathOr(
  emptyObject,
  [GC.SYSTEM_LIST_TELS],
  selectCurrentRouteInitialState(state),
);

const selectOwnTel = (_state, props = emptyObject) => props.tel;
const selectOwnTelGuid = (state, props) => G.getGuidFromObject(selectOwnTel(state, props));

const createStableResultSelector = (inputSelectors, projector) => {
  let previousResult;

  return createSelector(
    inputSelectors,
    (...values) => {
      const nextResult = projector(...values);

      if (R.equals(previousResult, nextResult)) return previousResult;

      previousResult = nextResult;

      return nextResult;
    },
  );
};

const getTelStops = (tel) => R.values(R.pathOr(emptyObject, [GC.FIELD_LOAD_STOPS], tel));

const getTelItemIds = (tel) => R.compose(
  R.uniq,
  R.reduce(R.concat, []),
  R.map(R.pathOr([], [GC.FIELD_STOP_ITEM_IDS])),
  getTelStops,
)(tel);

const getTelContainerIds = (tel, items) => {
  const stopContainers = R.reduce(
    (containers, stop) => R.concat(
      containers,
      R.concat(
        R.pathOr([], [GC.FIELD_STOP_PICKED_UP_CONTAINERS], stop),
        R.pathOr([], [GC.FIELD_STOP_DROPPED_CONTAINERS], stop),
      ),
    ),
    [],
    getTelStops(tel),
  );

  return R.compose(
    R.uniq,
    R.filter(G.isNotNil),
    R.map(R.prop(GC.FIELD_CONTAINER_INTERNAL_ID)),
    R.concat(stopContainers),
    R.values,
  )(items);
};

const getTelCloGuids = (tel) => R.compose(
  R.uniq,
  R.filter(G.isNotNil),
  R.concat(R.pathOr([], ['linkedCloGuids'], tel)),
  R.map(R.prop(GC.FIELD_CLO_GUID)),
  getTelStops,
)(tel);

const makeSelectBuilderStore = () => createSelector(
  selectBuilderStore,
  (state) => ({ ...state }),
);

const makeSelectInitRouteGuid = () => createSelector(
  selectBuilderStore,
  ({ initRouteGuid }) => initRouteGuid,
);

const makeSelectOpenedFromPage = () => createSelector(
  selectBuilderStore,
  ({ openedFromPage }) => openedFromPage,
);

const makeSelectEditTemplateRoute = () => createSelector(
  selectBuilderStore,
  ({ editTemplateRoute }) => editTemplateRoute,
);

const makeSelectCurrentRoute = () => selectCurrentRoute;

const makeSelectCurrentRouteGuid = () => createSelector(
  selectCurrentRoute,
  G.getGuidFromObject,
);

const makeSelectBranchGuidForRequest = () => createSelector(
  selectBuilderStore,
  ({ branchGuidForRequest }) => branchGuidForRequest,
);

const makeSelectCurrentBranchConfigs = () => createSelector(
  selectBuilderStore,
  ({ currentBranchConfigs }) => currentBranchConfigs,
);

const makeSelectCloReport = () => createSelector(
  selectBuilderStore,
  ({ cloReport }) => cloReport,
);

const makeSelectCrossDockCloReport = () => createSelector(
  selectBuilderStore,
  ({ crossDockCloReport }) => crossDockCloReport,
);

const makeSelectTelReport = () => createSelector(
  selectBuilderStore,
  ({ telReport }) => telReport,
);

const makeSelectInboundTelReport = () => createSelector(
  selectBuilderStore,
  ({ inboundTelReport }) => inboundTelReport,
);

const makeSelectOutboundTelReport = () => createSelector(
  selectBuilderStore,
  ({ outboundTelReport }) => outboundTelReport,
);

const makeSelectCloList = () => createSelector(
  selectBuilderStore,
  ({ cloList }) => cloList,
);

const makeSelectCrossDockCloList = () => createSelector(
  selectBuilderStore,
  ({ crossDockCloList }) => crossDockCloList,
);

const makeSelectTelList = () => createSelector(
  selectBuilderStore,
  ({ telList }) => telList,
);

const makeSelectInboundTelList = () => createSelector(
  selectBuilderStore,
  ({ inboundTelList }) => inboundTelList,
);

const makeSelectOutboundTelList = () => createSelector(
  selectBuilderStore,
  ({ outboundTelList }) => outboundTelList,
);

const makeSelectCloListLoading = () => createSelector(
  selectBuilderStore,
  ({ cloListLoading }) => cloListLoading,
);

const makeSelectCrossDockCloListLoading = () => createSelector(
  selectBuilderStore,
  ({ crossDockCloListLoading }) => crossDockCloListLoading,
);

const makeSelectTelListLoading = () => createSelector(
  selectBuilderStore,
  ({ telListLoading }) => telListLoading,
);

const makeSelectInboundTelListLoading = () => createSelector(
  selectBuilderStore,
  ({ inboundTelListLoading }) => inboundTelListLoading,
);

const makeSelectOutboundTelListLoading = () => createSelector(
  selectBuilderStore,
  ({ outboundTelListLoading }) => outboundTelListLoading,
);

const makeSelectCloPagination = () => createSelector(
  selectBuilderStore,
  ({ cloPagination }) => cloPagination,
);

const makeSelectCrossDockCloPagination = () => createSelector(
  selectBuilderStore,
  ({ crossDockCloPagination }) => crossDockCloPagination,
);

const makeSelectTelPagination = () => createSelector(
  selectBuilderStore,
  ({ telPagination }) => telPagination,
);

const makeSelectInboundTelPagination = () => createSelector(
  selectBuilderStore,
  ({ inboundTelPagination }) => inboundTelPagination,
);

const makeSelectOutboundTelPagination = () => createSelector(
  selectBuilderStore,
  ({ outboundTelPagination }) => outboundTelPagination,
);

const makeSelectCloTotal = () => createSelector(
  selectBuilderStore,
  ({ cloTotal }) => cloTotal,
);

const makeSelectCrossDockCloTotal = () => createSelector(
  selectBuilderStore,
  ({ crossDockCloTotal }) => crossDockCloTotal,
);

const makeSelectTelTotal = () => createSelector(
  selectBuilderStore,
  ({ telTotal }) => telTotal,
);

const makeSelectInboundTelTotal = () => createSelector(
  selectBuilderStore,
  ({ inboundTelTotal }) => inboundTelTotal,
);

const makeSelectOutboundTelTotal = () => createSelector(
  selectBuilderStore,
  ({ outboundTelTotal }) => outboundTelTotal,
);

const makeSelectDraftFilterParams = () => createSelector(
  selectBuilderStore,
  ({ draftFilterParams }) => draftFilterParams,
);

const makeSelectCloTableTitleFilters = () => createSelector(
  selectBuilderStore,
  ({ cloTableTitleFilters }) => cloTableTitleFilters,
);

const makeSelectTelTableTitleFilters = () => createSelector(
  selectBuilderStore,
  ({ telTableTitleFilters }) => telTableTitleFilters,
);

const makeSelectInboundTelTableTitleFilters = () => createSelector(
  selectBuilderStore,
  ({ inboundTelTableTitleFilters }) => inboundTelTableTitleFilters,
);

const makeSelectOutboundTelTableTitleFilters = () => createSelector(
  selectBuilderStore,
  ({ outboundTelTableTitleFilters }) => outboundTelTableTitleFilters,
);

const makeSelectCrossDockCloTableTitleFilters = () => createSelector(
  selectBuilderStore,
  ({ crossDockCloTableTitleFilters }) => crossDockCloTableTitleFilters,
);

const makeSelectCloTableTitleSortValues = () => createSelector(
  selectBuilderStore,
  ({ cloTitleSortValues }) => cloTitleSortValues,
);

const makeSelectTelTableTitleSortValues = () => createSelector(
  selectBuilderStore,
  ({ telTitleSortValues }) => telTitleSortValues,
);

const makeSelectInboundTelTableTitleSortValues = () => createSelector(
  selectBuilderStore,
  ({ inboundTelTitleSortValues }) => inboundTelTitleSortValues,
);

const makeSelectOutboundTelTableTitleSortValues = () => createSelector(
  selectBuilderStore,
  ({ outboundTelTitleSortValues }) => outboundTelTitleSortValues,
);

const makeSelectCrossDockCloTableTitleSortValues = () => createSelector(
  selectBuilderStore,
  ({ crossDockCloTitleSortValues }) => crossDockCloTitleSortValues,
);

const makeSelectAvailableCloReports = () => createSelector(
  selectBuilderStore,
  ({ availableCloReports }) => availableCloReports,
);

const makeSelectAvailableTelReports = () => createSelector(
  selectBuilderStore,
  ({ availableTelReports }) => availableTelReports,
);

const makeSelectRouteTelConfigs = () => createSelector(
  selectCurrentBranchConfigs,
  (configs) => G.getRouteTelConfigsForCreateClo(configs),
);

const makeSelectPrimaryReferenceAutogeneratedConfig = () => createSelector(
  selectCurrentBranchConfigs,
  (configs) => G.getConfigValueFromStore(
    GC.TEL_PRIMARY_REFERENCE_AUTOGENERATED,
    configs,
  ),
);

const makeSelectRouteConfigs = () => createSelector(
  selectCurrentBranchConfigs,
  (configs) => {
    const autoRouteName = G.getConfigValueFromStore(
      GC.GENERAL_ROUTE_NAME_AUTOGENERATED,
      configs,
    );

    return { autoRouteName };
  },
);

const makeSelectDraftRouteConfigs = () => createSelector(
  selectCurrentBranchConfigs,
  (configs) => {
    const autoDraftRouteName = G.getConfigValueFromStore(
      GC.GENERAL_DRAFT_ROUTE_NAME_AUTOGENERATED,
      configs,
    );
    const draftRoutePrefix = G.getConfigValueFromStore(
      GC.GENERAL_DRAFT_ROUTE_NAME_PREFIX,
      configs,
    );
    const draftRouteSequence = G.getConfigValueFromStore(
      GC.GENERAL_DRAFT_ROUTE_NAME_SEQUENCE,
      configs,
    );

    return {
      draftRoutePrefix,
      autoDraftRouteName,
      draftRouteSequence,
    };
  },
);

const makeSelectDefaultUomFields = () => createSelector(
  selectCurrentBranchConfigs,
  (configStore) => {
    let defaultValue = {};
    const uomSystem = G.getConfigValueFromStore(
      GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM,
      configStore,
    );

    if (R.and(G.isNotNil(uomSystem), G.isNotEmpty(uomSystem))) {
      defaultValue = GC.defaultSystemUoms[uomSystem];
    }

    return defaultValue;
  },
);

const makeSelectOptionsForSelect = () => createSelector(
  selectCurrentBranchConfigs,
  (configs) => {
    const serviceType = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      configs,
      GC.GENERAL_TRANSPORTATION_SERVICE_TYPE,
      true,
    );
    const transportationMode = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
      configs,
      GC.GENERAL_MODE_TRANSPORTATION,
      true,
    );

    return {
      serviceType,
      transportationMode,
    };
  },
);

const makeSelectCloByGuid = (guid) => createSelector(
  selectCloList,
  (state = []) => R.find(R.propEq(guid, GC.FIELD_GUID), state),
);

const makeSelectCrossDockCloByGuid = (guid) => createSelector(
  selectCrossDockCloList,
  (state = []) => R.find(R.propEq(guid, GC.FIELD_GUID), state),
);

const makeSelectItemsForPickup = () => createStableResultSelector(
  [selectCurrentRouteItems],
  (currentRouteItems) => R.filter(
    (item) => R.and(
      R.propEq(true, GC.FIELD_SELECTED, item),
      R.not(G.isLoadTypeClo(item)),
    ),
    R.values(currentRouteItems),
  ),
);

const makeSelectCurrentRouteItems = () => selectCurrentRouteItems;

const makeSelectCurrentRouteContainers = () => selectCurrentRouteContainers;

const makeSelectCurrentTel = () => createSelector(
  [selectCurrentRouteTels, selectOwnTelGuid, selectOwnTel],
  (routeTels, telGuid, ownTel) => R.propOr(ownTel, telGuid, routeTels),
);

const makeSelectCurrentTelItems = () => {
  const selectCurrentTel = makeSelectCurrentTel();

  return createStableResultSelector(
    [selectCurrentRouteItems, selectCurrentTel],
    (routeItems, tel) => R.pick(getTelItemIds(tel), routeItems),
  );
};

const makeSelectCurrentTelContainers = () => {
  const selectCurrentTel = makeSelectCurrentTel();
  const selectCurrentTelItems = makeSelectCurrentTelItems();

  return createStableResultSelector(
    [selectCurrentRouteContainers, selectCurrentTel, selectCurrentTelItems],
    (routeContainers, tel, telItems) => R.pick(
      getTelContainerIds(tel, telItems),
      routeContainers,
    ),
  );
};

const makeSelectCurrentTelClos = () => {
  const selectCurrentTel = makeSelectCurrentTel();

  return createStableResultSelector(
    [selectCurrentRouteClos, selectCurrentTel],
    (routeClos, tel) => R.pick(getTelCloGuids(tel), routeClos),
  );
};

const makeSelectCurrentTelChanged = () => {
  const selectCurrentTel = makeSelectCurrentTel();

  return createSelector(
    [selectCurrentTel, selectInitialRouteTels, selectOwnTelGuid],
    (tel, initialRouteTels, telGuid) => G.notEquals(
      tel,
      R.prop(telGuid, initialRouteTels),
    ),
  );
};

const makeSelectEventsInfo = () => createSelector(
  selectCurrentRouteTels,
  (routeTels) => {
    const tels = R.filter(
      (tel) => G.isNotNilAndNotEmpty(tel[GC.FIELD_LOAD_STOPS]),
      R.sortBy(
        R.prop(GC.FIELD_ORDER),
        R.values(routeTels),
      ),
    );

    if (G.isNilOrEmpty(tels)) {

      return {
        total: 0,
        lastEvent: null,
        firstEvent: null,
      };
    }

    return {
      total: R.reduce(
        (arr = [], tel) => R.concat(arr, R.values(tel[GC.FIELD_LOAD_STOPS])),
        [],
        tels,
      ).length,
      lastEvent: R.last(R.sortBy(
        R.prop(GC.FIELD_TEL_EVENT_INDEX),
        R.values(R.last(tels).events),
      )),
      firstEvent: R.head(R.sortBy(
        R.prop(GC.FIELD_TEL_EVENT_INDEX),
        R.values(R.head(tels).events),
      )),
    };
  },
);

const makeSelectTotalDistance = () => createSelector(
  selectCurrentRouteTels,
  (routeTels) => H.getRouteTotalDistance(routeTels, GC.FIELD_DISTANCE_TEL),
);

const makeSelectTotalWeight = () => createSelector(
  selectCurrentRouteItems,
  (currentRouteItems) => G.calcItemsTotalWeightWithoutQty(R.values(currentRouteItems)),
);

const makeSelectTotalQuantity = () => createSelector(
  selectCurrentRouteItems,
  (currentRouteItems) => G.calculateTotalQuantity(R.values(currentRouteItems)),
);

const makeSelectDraftList = () => createSelector(
  selectBuilderStore,
  ({ draftList }) => draftList,
);

const makeSelectDraftPagination = () => createSelector(
  selectBuilderStore,
  ({ draftsPagination }) => draftsPagination,
);

const makeSelectRouteErrors = () => selectRouteErrors;

const makeSelectRouteFinancial = () => createSelector(
  [selectCurrentRouteClos, selectCurrentRouteTels],
  (routeClos, routeTels) => {
    const closTotal = G.toFixed(G.calculateLoadsRatesTotalFromRates(R.values(routeClos)));
    const telsTotal = G.toFixed(G.calculateTelsRatesTotalFromCharges(R.values(routeTels)));
    const path = [GC.SYSTEM_OBJECT_RATE, GC.SYSTEM_OBJECT_NORMALIZED_TOTAL, GC.FIELD_CURRENCY];
    const normalizedCurrency = R.compose(
      R.path(path),
      R.find((tel) => G.isNotNil(R.path(path, tel))),
      R.values,
    )(routeTels);
    const total = G.toFixed(R.subtract(closTotal, telsTotal));

    return {
      total,
      clo: closTotal,
      tel: telsTotal,
      currency: R.or(normalizedCurrency, GC.DEFAULT_UI_CURRENCY),
    };
  },
);

const locationFieldsToPick = [
  GC.FIELD_TEMPLATE_ID,
  GC.FIELD_LOCATION_NAME,
  GC.FIELD_CITY,
  GC.FIELD_STATE,
  GC.FIELD_ADDRESS_1,
  GC.FIELD_ZIP,
];

const makeSelectExistedTerminalOptions = () => createStableResultSelector(
  [selectCurrentRouteTels],
  (routeTels) => R.compose(
    R.map((event) => ({
      value: R.prop(GC.FIELD_STOP_ID, event),
      label: G.concatLocationFields(R.prop(GC.FIELD_LOCATION, event), locationFieldsToPick),
    })),
    R.uniqBy(R.prop(GC.FIELD_STOP_ID)),
    R.reduce((acc, item) => R.concat(acc, item), []),
    R.map((tel) => R.compose(
      R.uniqBy(R.prop(GC.FIELD_STOP_ID)),
      R.filter(R.propEq(GC.STOP_TYPE_TERMINAL, GC.FIELD_STOP_TYPE)),
      R.values(),
      R.prop(GC.FIELD_LOAD_STOPS),
    )(tel)),
    R.values(),
  )(routeTels),
);

const makeSelectSelectedTels = () => createSelector(
  selectBuilderStore,
  ({ selectedTels }) => selectedTels,
);

const makeSelectCrossDockLocationList = () => createSelector(
  selectBuilderStore,
  ({ crossDockLocationList }) => crossDockLocationList,
);

const makeSelectInboundTerminals = () => createSelector(
  selectBuilderStore,
  ({ inboundTerminals }) => inboundTerminals,
);

const makeSelectOutboundTerminals = () => createSelector(
  selectBuilderStore,
  ({ outboundTerminals }) => outboundTerminals,
);

const makeSelectPlannerMode = () => createSelector(
  selectBuilderStore,
  ({ plannerMode }) => plannerMode,
);

const makeSelectRouteChanged = () => createSelector(
  [selectCurrentRoute, selectCurrentRouteInitialState],
  G.notEquals,
);

const makeSelectCurrentRouteInitialState = () => selectCurrentRouteInitialState;

const makeSelectSplittedLoadOptions = () => createSelector(
  selectBuilderStore,
  ({ splittedLoadOptions }) => splittedLoadOptions,
);

const makeSelectShouldOpenTerminalAdding = () => createSelector(
  selectBuilderStore,
  ({ shouldOpenTerminalAdding }) => shouldOpenTerminalAdding,
);

const makeSelectScheduleEditorHistory = () => createSelector(
  selectScheduleEditorHistory,
  (history) => history,
);

const makeSelectCloCreatedByFilter = () => createSelector(
  selectBuilderStore,
  ({ cloCreatedByFilter }) => cloCreatedByFilter,
);

const makeSelectTelCreatedByFilter = () => createSelector(
  selectBuilderStore,
  ({ telCreatedByFilter }) => telCreatedByFilter,
);

const makeSelectInboundTelCreatedByFilter = () => createSelector(
  selectBuilderStore,
  ({ inboundTelCreatedByFilter }) => inboundTelCreatedByFilter,
);

const makeSelectOutboundTelCreatedByFilter = () => createSelector(
  selectBuilderStore,
  ({ outboundTelCreatedByFilter }) => outboundTelCreatedByFilter,
);

const makeSelectCrossDockCloCreatedByFilter = () => createSelector(
  selectBuilderStore,
  ({ crossDockCloCreatedByFilter }) => crossDockCloCreatedByFilter,
);

export {
  makeSelectCloList,
  makeSelectTelList,
  makeSelectCloTotal,
  makeSelectTelTotal,
  makeSelectCloReport,
  makeSelectTelReport,
  makeSelectCloByGuid,
  makeSelectDraftList,
  makeSelectEventsInfo,
  makeSelectCurrentTel,
  makeSelectTotalWeight,
  makeSelectRouteErrors,
  makeSelectScheduleEditorHistory,
  makeSelectPlannerMode,
  makeSelectRouteConfigs,
  makeSelectBuilderStore,
  makeSelectCurrentRoute,
  makeSelectSelectedTels,
  makeSelectRouteChanged,
  makeSelectTotalDistance,
  makeSelectTotalQuantity,
  makeSelectCloPagination,
  makeSelectTelPagination,
  makeSelectInitRouteGuid,
  makeSelectOpenedFromPage,
  makeSelectItemsForPickup,
  makeSelectCloListLoading,
  makeSelectTelListLoading,
  makeSelectRouteFinancial,
  makeSelectInboundTelList,
  makeSelectCurrentTelClos,
  makeSelectRouteTelConfigs,
  makeSelectDraftPagination,
  makeSelectOutboundTelList,
  makeSelectInboundTelTotal,
  makeSelectCurrentTelItems,
  makeSelectDefaultUomFields,
  makeSelectOptionsForSelect,
  makeSelectInboundTelReport,
  makeSelectOutboundTelTotal,
  makeSelectInboundTerminals,
  makeSelectCurrentRouteGuid,
  makeSelectEditTemplateRoute,
  makeSelectDraftFilterParams,
  makeSelectDraftRouteConfigs,
  makeSelectCurrentRouteItems,
  makeSelectOutboundTelReport,
  makeSelectOutboundTerminals,
  makeSelectCurrentTelChanged,
  makeSelectCloCreatedByFilter,
  makeSelectTelCreatedByFilter,
  makeSelectSplittedLoadOptions,
  makeSelectAvailableCloReports,
  makeSelectAvailableTelReports,
  makeSelectCloTableTitleFilters,
  makeSelectTelTableTitleFilters,
  makeSelectBranchGuidForRequest,
  makeSelectCurrentBranchConfigs,
  makeSelectInboundTelPagination,
  makeSelectCurrentTelContainers,
  makeSelectInboundTelListLoading,
  makeSelectCrossDockLocationList,
  makeSelectOutboundTelPagination,
  makeSelectExistedTerminalOptions,
  makeSelectOutboundTelListLoading,
  makeSelectCurrentRouteContainers,
  makeSelectCloTableTitleSortValues,
  makeSelectTelTableTitleSortValues,
  makeSelectCurrentRouteInitialState,
  makeSelectShouldOpenTerminalAdding,
  makeSelectInboundTelCreatedByFilter,
  makeSelectOutboundTelCreatedByFilter,
  makeSelectInboundTelTableTitleFilters,
  makeSelectOutboundTelTableTitleFilters,
  makeSelectInboundTelTableTitleSortValues,
  makeSelectOutboundTelTableTitleSortValues,
  makeSelectPrimaryReferenceAutogeneratedConfig,
  // cross dock clos
  makeSelectCrossDockCloList,
  makeSelectCrossDockCloTotal,
  makeSelectCrossDockCloByGuid,
  makeSelectCrossDockCloReport,
  makeSelectCrossDockCloPagination,
  makeSelectCrossDockCloListLoading,
  makeSelectCrossDockCloCreatedByFilter,
  makeSelectCrossDockCloTableTitleFilters,
  makeSelectCrossDockCloTableTitleSortValues,
};
